/**
 * The requests behind attaching photos to a proposal or invoice, moved here unchanged from
 * `ProposalInvoicePhotoSelector` so the desktop project record's inline photo picker makes the
 * same ones:
 * - a job-note photo is copied (HEIC converted, compressed) into the document's bucket, gets a
 *   File record, then an EventPhoto on the document (`copyNotePhotoToDocument`, in its own file);
 * - an upload goes up as an internal job note first (`buildUploadMemoRequest`), and each of its
 *   files is then promoted the same way (`promoteUploadedFileToDocument`);
 * - leaving a photo off (or putting it back) is a masked EventPhoto update of its include flag
 *   (`buildPhotoVisibilityUpdate`).
 */
import {
  type CompressionOptions,
  type CreateAndUploadMemoReq,
  EventPhoto,
  File as KalosFile,
  FileObject,
  Memo,
  URLObject,
} from '@kalos/kalos-rpc';

import { type PhotoVisibilityFlag } from '../../../hooks/react-query/useEventPhotoGalleryPreviews';
import {
  generateUniqueFileName,
  prepareImageFileForMemoStagingUpload,
  runWithConcurrency,
} from '../../../tools/fileUploadUtils';
import {
  convertHeicToJpeg,
  easternTimestamp,
  EventClientService,
  FileClientService,
  getEnhancedMimeType,
  S3ClientService,
} from '../../../tools/helpers';

/** Limit parallel client-side prepare (decode/resize) to reduce memory spikes on mobile. */
export const PREPARE_CONCURRENCY = 3;
/** Limit parallel S3 promote + createFile + createEventPhoto to reduce contention on slow links. */
export const PROMOTE_CONCURRENCY = 3;

// Valid image MIME types
const VALID_IMAGE_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
];

// Helper function to validate if a file is a valid image type
export const isValidImageFile = async (file: File): Promise<boolean> => {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const fileData = new Uint8Array(arrayBuffer);
    const enhancedMimeType = getEnhancedMimeType(file.name, fileData);

    return VALID_IMAGE_TYPES.includes(enhancedMimeType);
  } catch (error) {
    console.error('Error validating file type:', error);
    return false;
  }
};

/** One memo-attached image file (metadata only; preview loaded per card). */
export interface MemoImageListItem {
  memoId: number;
  fileId: number;
  fileName: string;
  fileBucket: string;
  fileMimeType: string;
  memoDescription: string;
  memoDate: string;
}

export async function buildMemoImagePreviewDataUrl(file: {
  name: string;
  bucket: string;
}): Promise<string | null> {
  try {
    const url = URLObject.create({
      bucket: file.bucket,
      key: file.name,
    });
    const urlRes = await S3ClientService.GetDownloadURL(url);
    if (!urlRes?.url) return null;

    const downloadRes = await fetch(urlRes.url);
    if (downloadRes.status !== 200) return null;

    const arrayBuffer = await downloadRes.arrayBuffer();
    const uint8Array = new Uint8Array(arrayBuffer);
    let binaryString = '';
    for (let i = 0; i < uint8Array.length; i++) {
      binaryString += String.fromCharCode(uint8Array[i]);
    }
    const base64String = btoa(binaryString);

    const enhancedMimeType = getEnhancedMimeType(file.name, uint8Array);
    const isHeic = enhancedMimeType === 'image/heic' || enhancedMimeType === 'image/heif';

    if (isHeic) {
      try {
        const convertedBlob = await convertHeicToJpeg(uint8Array, file.name, enhancedMimeType);
        const convertedBuffer = await convertedBlob.arrayBuffer();
        const convertedData = new Uint8Array(convertedBuffer);
        let convertedBinaryString = '';
        for (let i = 0; i < convertedData.length; i++) {
          convertedBinaryString += String.fromCharCode(convertedData[i]);
        }
        const convertedBase64String = btoa(convertedBinaryString);
        return `data:image/jpeg;base64,${convertedBase64String}`;
      } catch (error) {
        console.error('Error converting HEIC for preview:', error);
        return `data:${enhancedMimeType};base64,${base64String}`;
      }
    }

    return `data:${enhancedMimeType};base64,${base64String}`;
  } catch (error) {
    console.error('Error preparing memo photo preview:', error);
    return null;
  }
}

/** Where attached photos go: the job, the document filter, its bucket and file name prefix. */
export type DocumentPhotoTarget = {
  eventId: number;
  filter: Partial<EventPhoto>;
  bucket: string;
};

// Helper to generate filename prefix based on filter type
export const documentPhotoFilenamePrefix = ({ eventId, filter }: DocumentPhotoTarget) => {
  if (filter.quoteDocumentId) {
    return `proposal-${filter.quoteDocumentId}`;
  }
  if (filter.invoiceId) {
    return `invoice-${filter.invoiceId}`;
  }
  return `photo-${eventId}`;
};

/**
 * Note photos not on this document yet. Adding copies the file under a new name that ends with
 * the original's (see `copyNotePhotoToDocument`), so a photo counts as added when one of this
 * document's files is that file or carries its name.
 */
const fileStem = (name: string) => name.replace(/\.[^/.]+$/, '');
export const isNotePhotoFile = (item: MemoImageListItem, file: { id: number; name: string }) =>
  file.id === item.fileId || fileStem(file.name).endsWith(`-${fileStem(item.fileName)}`);

/** Prepare picked files (decode, resize) as staged file objects for the upload memo. */
export const prepareUploadFileObjects = (validFiles: File[]) =>
  runWithConcurrency(validFiles, PREPARE_CONCURRENCY, async (file) => {
    const prepared = await prepareImageFileForMemoStagingUpload(file);
    const stem = file.name.replace(/\.[^/.]+$/, '') || 'photo';
    const uniqueName = generateUniqueFileName(`${stem}.${prepared.extension}`);
    return FileObject.create({
      bucket: 'testbuckethelios',
      key: uniqueName,
      mimeType: prepared.mimeType,
      data: prepared.data,
    });
  });

/** The internal job note an upload goes up as. */
export const buildUploadMemoRequest = ({
  validFileCount,
  imageFileObjects,
  loggedUserId,
  eventId,
}: {
  validFileCount: number;
  imageFileObjects: FileObject[];
  loggedUserId: number;
  eventId: number;
}): CreateAndUploadMemoReq => ({
  memo: Memo.create({
    description: `Photo upload - ${validFileCount} photo(s) added`,
    createdBy: loggedUserId,
    userId: loggedUserId,
    isActive: true,
    statusId: 1,
    eventId: eventId,
    createdDate: easternTimestamp(),
    classificationId: 1, // Default to general classification
    // Bookkeeping, not visit documentation. An external memo on a
    // ServiceChannel job is transmitted to the subscriber's work order.
    isInternal: true,
  }),
  files: {
    files: imageFileObjects,
  },
});

/** Copy one uploaded memo file into the document's bucket and attach it. Throws on failure. */
export const promoteUploadedFileToDocument = async (
  file: { bucket: string; name: string; mimeType: string },
  target: DocumentPhotoTarget,
): Promise<EventPhoto> => {
  const { eventId, filter, bucket } = target;
  const newFileName = `${documentPhotoFilenamePrefix(target)}-${Date.now()}-${file.name}`;

  const source = {
    bucket: file.bucket,
    key: file.name,
  };

  const destination = {
    bucket: bucket,
    key: newFileName,
  };

  const isWebP = file.mimeType === 'image/webp' || file.name.toLowerCase().endsWith('.webp');

  let copyResult: string;
  let finalMimeType: string;

  if (isWebP) {
    copyResult = await S3ClientService.moveFileBetweenS3Buckets(source, destination, true);
    finalMimeType = 'image/webp';
  } else {
    const compressionOptions: CompressionOptions = {
      maxWidth: 1200,
      maxHeight: 1200,
      quality: 80,
      format: 'JPEG',
    };

    copyResult = await S3ClientService.compressAndMoveFile(
      source,
      destination,
      compressionOptions,
      true,
    );
    finalMimeType = 'image/jpeg';
  }

  if (copyResult === 'nok') {
    throw new Error(`Failed to ${isWebP ? 'move' : 'compress and copy'} file: ${file.name}`);
  }

  const fileRes = await FileClientService.Create(
    KalosFile.create({
      name: newFileName,
      bucket: bucket,
      mimeType: finalMimeType,
    }),
  );

  if (!fileRes || !fileRes.id) {
    throw new Error(`Failed to create file record for: ${newFileName}`);
  }

  const isProposalContext = !!filter.quoteDocumentId;
  const isInvoiceContext = !!filter.invoiceId;

  const eventPhoto = EventPhoto.create({
    eventId,
    fileId: fileRes.id,
    ...(isProposalContext && { quoteDocumentId: filter.quoteDocumentId }),
    ...(isInvoiceContext && { invoiceId: filter.invoiceId }),
    includeInProposal: isProposalContext,
    includeInInvoice: isInvoiceContext,
  });

  const completePhoto = await EventClientService.CreateEventPhoto(eventPhoto);
  return completePhoto;
};

// The field mask is what makes this safe: pbsql drops false booleans on INSERT,
// so a flag can only be turned OFF through a masked update like this one.
const visibilityFieldMask: Record<PhotoVisibilityFlag, string> = {
  includeInProposal: 'IncludeInProposal',
  includeInInvoice: 'IncludeInInvoice',
  includeInServiceChannel: 'IncludeInServiceChannel',
};

/** Show or leave off a photo on every destination in `flags`, as one masked update. */
export const buildPhotoVisibilityUpdate = (
  photoId: number,
  shown: boolean,
  flags: PhotoVisibilityFlag[],
) =>
  EventPhoto.create({
    id: photoId,
    ...Object.fromEntries(flags.map((flag) => [flag, shown])),
    fieldMask: flags.map((flag) => visibilityFieldMask[flag]),
  });
